import { TablizoCustom } from "Components/LabIZO/Tablizo";
import { Info, Add, Edit } from "./schema/form-schema";
import { Typography } from "@mui/material";
import { FormizoSchema } from "Components/LabIZO/Formizo";
import { FormizoCustom } from "Components/LabIZO/Formizo/FormizoSchema.interface";
import { VStack } from "Components/LabIZO/Stackizo";
import { store } from "static";
import { Filterables } from "./schema/filter-schema";

/** ★ English Toolbar injected above the main Answer textarea */
import HtmlToolbar from "./HtmlToolbar";

/** ===== Table columns ===== */
const Table: TablizoCustom = (tbLang: { display: string; value: string }) => [
  {
    label: "ID",
    name: "name",
    filterable: true,
    hide: true,
  },
  {
    label: "FAQ ID",
    name: "alias",
    filterable: true,
    defaultSort: "asc",
  },
  {
    label: `Button Caption (${tbLang.value})`,
    name: `intent.${tbLang.value}.description`,
    filterable: true,
  },
  {
    label: `Question (${tbLang.value})`,
    name: `intent.${tbLang.value}.examples.0.text`,
    filterable: true,
  },
  {
    label: `Answer (${tbLang.value})`,
    name: `answer.text.${tbLang.value}.0`,
    filterable: true,
  },
  {
    label: `Created Time`,
    name: `CREATED_AT`,
    filterable: false,
  },
  {
    label: `Last Updated Time`,
    name: `LASTMODIFIED`,
    filterable: false,
  },
];

/** ===== Quick Reply sub-form ===== */
export const QuickReply: FormizoSchema = {
  label: "Quick Reply Buttons",
  name: "answer.quickReplies",
  canAdd: true,
  canDelete: true,
  headerStyle: "outlined",
  addStyle: "header",
  array: [
    {
      label: "Display",
      width: 50,
      name: " ",
      tabs: [
        {
          label: "EN",
          page: [
            {
              label: "Display",
              name: "title.EN",
              format: "text",
            },
          ],
        },
        {
          label: "TC",
          page: [
            {
              label: "Display",
              name: "title.TC",
              format: "text",
            },
          ],
        },
        {
          label: "SC",
          page: [
            {
              label: "Display",
              name: "title.SC",
              format: "text",
            },
          ],
        },
      ],
    },
    {
      label: "Button Payload",
      name: "payload",
      format: "select",
      selectStyle: "dropdown",
      selectRef: "intents",
      selectCap: "caption",
      selectVal: "_id",
      placeholder: "Intent",
      validate: ["required"],
      variant: "grid",
      width: 250,
    },
  ],
};

/** ===== Following Intent sub-form ===== */
export const FollowingIntent: FormizoSchema = {
  label: "Suggested Questions",
  name: "answer.followingIntent",
  canAdd: true,
  canDelete: true,
  headerStyle: "outlined",
  addStyle: "header",
  array: [
    {
      label: "Display",
      width: 50,
      name: "title",
      tabs: [
        {
          label: "EN",
          page: [
            {
              label: "Display",
              name: "title.EN",
              format: "text",
            },
          ],
        },
        {
          label: "TC",
          page: [
            {
              label: "Display",
              name: "title.TC",
              format: "text",
            },
          ],
        },
        {
          label: "SC",
          page: [
            {
              label: "Display",
              name: "title.SC",
              format: "text",
            },
          ],
        },
      ],
    },
    {
      label: "Button Payload",
      name: "payload",
      format: "select",
      selectStyle: "dropdown",
      selectRef: "intents",
      selectCap: "caption",
      selectVal: "_id",
      placeholder: "Intent",
      validate: ["required"],
      variant: "grid",
      width: 250,
    },
  ],
};

/** ===== Questions form (current language first) ===== */
const Questions: FormizoCustom = (formValue, addOns, fLang: { display: string; value: string }) => [
  {
    label: `${fLang.display} (${fLang.value})`,
    name: `intent.${fLang.value}.examples.0.text`,
    format: "text",
    validate: ["required"],
  },
  {
    label: "Other Languages",
    foldStyle: "none",
    collapse: store.sysInfo.Language.available.map((o) => {
      if (o.value === fLang.value) return <div />;
      return {
        label: `${o.display}`,
        name: `intent.${o.value}.examples.0.text`,
        format: "text",
      };
    }),
  },
];

/**
 * ===== Answers form with EN Toolbar injected =====
 * 支援第四參數 textareaId（預設 "faq-edit-answer"），避免多處同時渲染時的 DOM 衝突。
 */
const Answers: (formValue: any, addOns: any, fLang: { display: string; value: string }) => any[] = (formValue, addOns, fLang) => [
  // ★ 主要語言工具列
  { inject: <HtmlToolbar /> },

  {
    label: `${fLang.display} (${fLang.value})`,
    name: `answer.text.${fLang.value}.0`,
    format: "textarea",
    validate: ["required"],
    rows: 5,
    placeholder: "Enter answer here...",
  },

  {
    label: "Other Languages",
    foldStyle: "none",
    // ★ 其他語言也各自注入工具列
    collapse: store.sysInfo.Language.available
      .filter((o) => o.value !== fLang.value)
      .flatMap((o) => [
        { inject: <HtmlToolbar /> },
        {
          label: `${o.display}`,
          name: `answer.text.${o.value}.0`,
          format: "textarea",
          rows: 5,
          placeholder: "Enter answer here...",
        },
      ]),
  },

  QuickReply,
  FollowingIntent,
];

/** ===== Settings ===== */
const Settings: FormizoSchema[] = [
  {
    inject: (
      <VStack alignItems="flex-start" width="100%">
        <Typography style={{ fontSize: 9 }}>*ID will add the "FAQ_" prefix to associated component IDs.</Typography>
        <Typography style={{ fontSize: 9 }}>*Only alphanumeric characters (a-z, A-Z, 0-9) and underscore (_) are allowed.</Typography>
      </VStack>
    ),
  },
  {
    label: "ID",
    name: "id",
    validate: ["required", "plain"],
    format: "text",
  },
  {
    label: "Advanced Settings",
    collapse: [
      {
        label: "Alias",
        name: "alias",
        format: "text",
        placeholder: "(Optional) If empty, Alias will be the same as ID.",
      },
      {
        label: "Description",
        name: "description",
        format: "text",
        placeholder: "(Optional) If empty, Description will be the same as Alias.",
      },
      {
        label: "Button Caption (EN)",
        name: "btn.EN",
        format: "text",
        placeholder: "(Optional) If empty, Caption will follow Description.",
      },
      {
        label: "Button Caption (TC)",
        name: "btn.TC",
        format: "text",
        placeholder: "(Optional) If empty, Caption will follow Description.",
      },
      {
        label: "Button Caption (SC)",
        name: "btn.SC",
        format: "text",
        placeholder: "(Optional) If empty, Caption will follow Description.",
      },
    ],
  },
];

/** ===== export ===== */
const schema = {
  Table,
  Info,
  Add,
  Edit, // 這個來自 ./schema/form-schema（你現有檔）
  Questions,
  Answers, // ★ 新版 Answers（含 Toolbar）
  Settings,
  Filterables,
};

export default schema;
